import { useCallback, useMemo } from 'react'

import useAuth from 'hooks/use-auth'
import { useGetTeams } from 'hooks/use-get-teams-paged'
import { useGetSharedAssets } from 'queries/sharedAssets/use-get-shared-assets'
import { SharedAssetRequestStatus } from 'types/api/sharedAssets/requests'
import {
  type SharedAssetsResponse,
  type IncomingRequest,
  type OutgoingRequest,
  type OwnedK8sClusterAsset,
  type AvailableK8sClusterAsset
} from 'types/api/sharedAssets/sharedAssets'
import { type Team } from 'types/api/teams'
import { createLookupMapFromArray } from 'utils/obj'
import { ms } from 'utils/time/millisecond'

export interface IncomingRequestByAssetId extends IncomingRequest {
  clusterName: string
  owningTeamId: string
}

export interface OutgoingRequestByAssetId extends OutgoingRequest {
  assetId: string
  clusterName: string
  owningTeamId: string
  owningTeamName: string
}

export type IncomingRequestsByAssetId = {
  [key: string]: IncomingRequestByAssetId[]
}

export type OutgoingRequestsByAssetId = {
  [key: string]: OutgoingRequestByAssetId[]
}

type ManageSharedAssetsValues = {
  sharedAssets: SharedAssetsResponse | undefined
  isLoadingSharedAssets: boolean
  isErrorSharedAssets: boolean
  incomingRequestsByAssetId: IncomingRequestsByAssetId
  outgoingRequestsByAssetId: OutgoingRequestsByAssetId
  pendingIncomingRequestsByAssetId: IncomingRequestsByAssetId
  pendingOutgoingRequestsByAssetId: OutgoingRequestsByAssetId
  allTeamsLookupTableById: { [key: string]: Team }
  getOwnedClusterById: (assetId: string) => OwnedK8sClusterAsset | undefined
  getAvailableClusterById: (
    assetId: string
  ) => AvailableK8sClusterAsset | undefined
  getAllIncomingRequestsForCluster: (
    assetId: string
  ) => IncomingRequestByAssetId[]
  getPendingIncomingRequestsForCluster: (
    assetId: string
  ) => IncomingRequestByAssetId[]
  getAllOutgoingRequestsForCluster: (
    assetId: string
  ) => OutgoingRequestByAssetId[]
  getPendingOutgoingRequestsForCluster: (
    assetId: string
  ) => OutgoingRequestByAssetId[]
}

export function useManageSharedAssets(): ManageSharedAssetsValues {
  const { currentTeamId } = useAuth()
  const { data: allTeams } = useGetTeams()

  const {
    data: sharedAssets,
    isLoading: isLoadingSharedAssets,
    isError: isErrorSharedAssets
  } = useGetSharedAssets({
    refetchInterval: ms.seconds(15)
  })

  // Isolate incoming cluster asset requests and map them to assetId
  const incomingRequestsByAssetId = useMemo(() => {
    return (sharedAssets?.myClusters ?? []).reduce(
      (acc: IncomingRequestsByAssetId, cluster: OwnedK8sClusterAsset) => {
        // Adds the clusterName and owningTeamId to the request object
        acc[cluster.assetId] = cluster.requests.map((request) => ({
          ...request,
          clusterName: cluster.clusterName,
          owningTeamId: cluster.owningTeamId
        }))

        return acc
      },
      {}
    )
  }, [sharedAssets?.myClusters])

  // Isolate outgoing cluster asset requests and map them to assetId
  const outgoingRequestsByAssetId = useMemo(() => {
    return (sharedAssets?.availableClusters ?? []).reduce(
      (acc: OutgoingRequestsByAssetId, cluster: AvailableK8sClusterAsset) => {
        // Adds the clusterName, owningTeamId, and owningTeamName to the request object
        acc[cluster.assetId] = cluster.requests.map((request) => ({
          ...request,
          assetId: cluster.assetId,
          clusterName: cluster.clusterName,
          owningTeamId: cluster.owningTeamId,
          owningTeamName: cluster.owningTeamName,
          sharedTeam: currentTeamId
        }))

        return acc
      },
      {}
    )
  }, [currentTeamId, sharedAssets?.availableClusters])

  // Get all incoming access requests that are still pending approval and map them to assetId
  const pendingIncomingRequestsByAssetId = useMemo(() => {
    return Object.entries(incomingRequestsByAssetId ?? {}).reduce(
      (
        acc: IncomingRequestsByAssetId,
        [assetId, allIncomingRequests]: [string, IncomingRequestByAssetId[]]
      ) => {
        const pendingIncomingRequests = allIncomingRequests.filter(
          (request) =>
            request.currentStatus === SharedAssetRequestStatus.REQUESTED
        )

        if (pendingIncomingRequests.length > 0) {
          acc[assetId] = pendingIncomingRequests
        }

        return acc
      },
      {}
    )
  }, [incomingRequestsByAssetId])

  // Get all outgoing access requests that are still pending approval and map them to assetId
  const pendingOutgoingRequestsByAssetId = useMemo(() => {
    return Object.entries(outgoingRequestsByAssetId).reduce(
      (
        acc: OutgoingRequestsByAssetId,
        [assetId, allOutgoingRequests]: [string, OutgoingRequestByAssetId[]]
      ) => {
        const pendingOutgoingRequests = allOutgoingRequests.filter(
          (request) =>
            request.currentStatus === SharedAssetRequestStatus.REQUESTED
        )

        if (pendingOutgoingRequests.length > 0) {
          acc[assetId] = pendingOutgoingRequests
        }

        return acc
      },
      {}
    )
  }, [outgoingRequestsByAssetId])

  // Maps all teams to their identifiers
  const allTeamsLookupTableById = useMemo(
    () => createLookupMapFromArray<Team>(allTeams, 'identifier'),
    [allTeams]
  )

  // Get an owned cluster by assetId
  const getOwnedClusterById = useCallback(
    (assetId: string) => {
      return sharedAssets?.myClusters.find(
        (cluster) => cluster.assetId === assetId
      )
    },
    [sharedAssets?.myClusters]
  )

  // Get a cluster available to be shared by assetId
  const getAvailableClusterById = useCallback(
    (assetId: string) => {
      return sharedAssets?.availableClusters.find(
        (cluster) => cluster.assetId === assetId
      )
    },
    [sharedAssets?.availableClusters]
  )

  // Get all incoming requests for the current cluster based on the assetId
  const getAllIncomingRequestsForCluster = useCallback(
    (assetId: string) => {
      return incomingRequestsByAssetId[assetId] ?? []
    },
    [incomingRequestsByAssetId]
  )

  // Get all pending incoming requests for the current cluster based on the assetId
  const getPendingIncomingRequestsForCluster = useCallback(
    (assetId: string) => {
      return pendingIncomingRequestsByAssetId[assetId] ?? []
    },
    [pendingIncomingRequestsByAssetId]
  )

  // Get all incoming requests for the current cluster based on the assetId
  const getAllOutgoingRequestsForCluster = useCallback(
    (assetId: string) => {
      return outgoingRequestsByAssetId[assetId] ?? []
    },
    [outgoingRequestsByAssetId]
  )

  // Get all pending incoming requests for the current cluster based on the assetId
  const getPendingOutgoingRequestsForCluster = useCallback(
    (assetId: string) => {
      return pendingOutgoingRequestsByAssetId[assetId] ?? []
    },
    [pendingOutgoingRequestsByAssetId]
  )

  return {
    sharedAssets,
    isLoadingSharedAssets,
    isErrorSharedAssets,
    incomingRequestsByAssetId,
    outgoingRequestsByAssetId,
    pendingIncomingRequestsByAssetId,
    pendingOutgoingRequestsByAssetId,
    allTeamsLookupTableById,
    getOwnedClusterById,
    getAvailableClusterById,
    getAllIncomingRequestsForCluster,
    getPendingIncomingRequestsForCluster,
    getAllOutgoingRequestsForCluster,
    getPendingOutgoingRequestsForCluster
  }
}
